import {
  Button,
  DEFAULT_PAGE_WIDTH,
  Page,
  SearchBar,
  Text,
  TextButton,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import { useState } from "react";
import { gestureHandlerRootHOC } from "react-native-gesture-handler";
import { SceneMap, TabBar, TabView } from "react-native-tab-view";
import { Spacings, View } from "react-native-ui-lib";
import { SearchContext, useSearch } from "../../../../utilities/search";
import { StockCountTabProvider } from "../../components";
import { StockPendingListTab } from "./tabs";
import { StockCompletedListTab } from "./tabs/StockCompletedListTab";
import { StockCountTabs } from "./constants";
import React from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { StockCountsRootParamList } from "../../../../types";
import { useTranslation } from "../../../../utilities/i18n";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import { StockCountFilterValues } from "../../types";
import { useFocusEffect } from "@react-navigation/native";
import { AntDesign } from "@expo/vector-icons";
import { renderTabBar } from "../../../../components/TabBar";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";

const renderScene = SceneMap({
  pending: StockPendingListTab,
  completed: StockCompletedListTab,
});

const indexToKey: Record<number, StockCountTabs> = {
  0: StockCountTabs.PENDING,
  1: StockCountTabs.COMPLETED,
};

export type StockCountsScreenProps = StackScreenProps<
  StockCountsRootParamList,
  "StockCountsScreen"
>;

function StockCountsScreenInner({ navigation }: StockCountsScreenProps) {
  const { t } = useTranslation("stock_counts");

  const search = useSearch();
  const [index, setIndex] = useState(0);
  const [selectedKey, setSelectedKey] = useState(StockCountTabs.PENDING); // get immediate access to current tab
  const { filterState } = useListFilterContext<StockCountFilterValues>();
  const { activeFiltersCount } = filterState;
  const transitionEnded = useHasTransitionEnded(navigation);
  const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 });

  // if there is no space at the end, the renderer will put the last character on the next line
  const [routes] = useState([
    { key: StockCountTabs.PENDING, title: "Pending " },
    { key: StockCountTabs.COMPLETED, title: "Completed " },
  ]);

  const handleIndexChange = (index: number) => {
    setIndex(index);
    setSelectedKey(indexToKey[index]);
  };

  useFocusEffect(
    React.useCallback(() => {
      navigation.setOptions({
        headerTitle() {
          return (
            <TextButton
              iconStyle={{ marginRight: 0 }}
              icon={<AntDesign name="filter" size={20} />}
              onPress={() => {
                navigation.navigate("StockCountFilterRootScreen", {
                  screen: "StockCountFilterScreen",
                });
              }}
            >
              {t("common:filter", {
                count: activeFiltersCount,
              })}
            </TextButton>
          );
        },
      });
    }, [activeFiltersCount])
  );

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          label={t("button.start_count")}
          isAction
          size="compact"
          onPress={() =>
            navigation.navigate("StartStockCountRootScreen", {
              screen: "StartStockCountScreen",
            })
          }
        />
      ),
    });
  }, []);

  if (false == transitionEnded) {
    return (
      <Page>
        <FlatListSearchableSkeleton searchbar={true} />
      </Page>
    );
  }

  const maxWidth =
    dimensions.width > DEFAULT_PAGE_WIDTH
      ? DEFAULT_PAGE_WIDTH
      : dimensions.width;

  const tabContent =
    dimensions.width > 0 ? (
      <TabView
        renderTabBar={renderTabBar}
        navigationState={{ index, routes }}
        renderScene={renderScene}
        onIndexChange={setIndex}
        initialLayout={{
          width: maxWidth,
        }}
        style={{
          maxWidth: maxWidth,
          alignSelf: "center",
          width: "100%",
        }}
      />
    ) : undefined;

  return (
    <StockCountTabProvider {...{ index, selectedKey }}>
      <Page padding={0} fullWidth scrollable={false}>
        <SearchContext.Provider value={search}>
          <View
            style={{
              marginTop: Spacings.s6,
              maxWidth: DEFAULT_PAGE_WIDTH,
              width: "100%",
              alignSelf: "center",
              paddingHorizontal: Spacings.s6,
            }}
            onLayout={(event) => {
              const { width, height } = event.nativeEvent.layout;
              setDimensions({ width, height });
            }}
          >
            <SearchBar
              placeholder="Search..."
              onChange={search.setQuery}
              value={search.query}
            />
          </View>
          {tabContent}
        </SearchContext.Provider>
      </Page>
    </StockCountTabProvider>
  );
}

export const StockCountsScreen = gestureHandlerRootHOC(StockCountsScreenInner);
